import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { fetchActivity } from '@/lib/admin/activity';

export default function ActivityPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userId, setUserId] = useState(searchParams.get('userId') ?? '');
  const [type, setType] = useState(searchParams.get('type') ?? '');
  const [from, setFrom] = useState(searchParams.get('from') ?? '');
  const [to, setTo] = useState(searchParams.get('to') ?? '');
  const page = Number(searchParams.get('page')) || 1;
  const perPage = 50;

  const { data } = useQuery({
    queryKey: ['admin', 'activity', { userId, type, from, to, page }],
    queryFn: () =>
      fetchActivity({
        userId: userId || undefined,
        type: type || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        perPage,
      }),
    placeholderData: keepPreviousData,
  });

  const apply = () => {
    const next = new URLSearchParams();
    if (userId) next.set('userId', userId);
    if (type) next.set('type', type);
    if (from) next.set('from', from);
    if (to) next.set('to', to);
    setSearchParams(next);
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.activity')}</h1>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
          <input className="rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder={t('admin.filters.userId')} value={userId} onChange={(e) => setUserId(e.target.value)} />
          <input className="rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder={t('admin.filters.type')} value={type} onChange={(e) => setType(e.target.value)} />
          <input type="date" className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
          <button className="rounded-lg bg-primary-700 px-3 py-2 text-sm font-medium text-white" onClick={apply}>{t('common.filter')}</button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2">{t('admin.columns.type')}</th>
              <th className="px-4 py-2">{t('admin.columns.user')}</th>
              <th className="px-4 py-2">{t('admin.columns.description')}</th>
              <th className="px-4 py-2">{t('admin.columns.when')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">{t('common.noResults')}</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-2 text-xs font-semibold uppercase text-gray-700">{a.type}</td>
                <td className="px-4 py-2 text-xs text-gray-600">{a.user?.email ?? '—'}</td>
                <td className="px-4 py-2">{a.description ?? '—'}</td>
                <td className="px-4 py-2 text-xs text-gray-500" title={new Date(a.createdAt).toISOString()}>
                  {formatDistanceToNow(new Date(a.createdAt), { addSuffix: true })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
          <span>{t('common.showing')} {items.length} {t('common.of')} {total}</span>
          <div className="flex items-center gap-2">
            <button className="rounded border border-gray-200 px-3 py-1 disabled:opacity-50" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
            <span>{page} / {totalPages}</span>
            <button className="rounded border border-gray-200 px-3 py-1 disabled:opacity-50" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</button>
          </div>
        </div>
      </div>
    </div>
  );
}
